import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { EMPTY, Observable, forkJoin } from 'rxjs';
import { expand, map, reduce } from 'rxjs/operators';

import { CompaniesService } from '../companies/companies.service';
import { SectorsService } from '../sectors/sectors.service';
import { ICheckEmployeeResponse } from '../../interfaces/check-employee-response.interface';
import { IQualification, IUserQualification, IUserQualificationsParams, IWorkTypesResponse } from './users';

@Injectable()
export class UsersService {
  private readonly workTypesPageLimit = 30;

  constructor(
    private http: HttpClient,
    private companiesService: CompaniesService,
    private plotsService: SectorsService
  ) {}

  /**
   * Квалификации сотрудников целиком: эндпоинт страничный,
   * догружаем, пока приходит полная страница
   */
  getQualifications(): Observable<IQualification[]> {
    const limit = this.workTypesPageLimit;
    let offset = 0;

    return this.getWorkTypesPage(limit, offset).pipe(
      expand((workTypes) => {
        if (workTypes.length < limit) {
          return EMPTY;
        }

        offset += workTypes.length;

        return this.getWorkTypesPage(limit, offset);
      }),
      reduce((acc: IQualification[], workTypes: IQualification[]) => acc.concat(workTypes), [] as IQualification[])
    );
  }

  private getWorkTypesPage(limit: number, offset: number): Observable<IQualification[]> {
    const params = new HttpParams().set('limit', limit.toString()).set('offset', offset.toString());

    return this.http.get<IWorkTypesResponse>(`/schedules/work_types/`, { params }).pipe(
      map((response) => {
        const workTypes = (response && response.data && response.data.work_types) || [];

        return workTypes.map((workType) => ({ id: workType.id, name: workType.description || String(workType.id) }));
      })
    );
  }

  /**
   * Квалификации сотрудников. user_id и work_type_id метод принимает
   * списком через запятую, а не повторяющимися параметрами
   */
  getUserQualifications(params?: IUserQualificationsParams): Observable<IUserQualification[]> {
    let httpParams = new HttpParams();

    if (params && params.user_id && params.user_id.length) {
      httpParams = httpParams.set('user_id', params.user_id.join(','));
    }
    if (params && params.work_type_id && params.work_type_id.length) {
      httpParams = httpParams.set('work_type_id', params.work_type_id.join(','));
    }

    return this.http
      .get<any>(`/admin/users/qualifications/`, { params: httpParams })
      .pipe(map((response) => this.extractUserQualifications(response)));
  }

  /**
   * Обёртку ответа подтвердить не удалось, поэтому допускаем и плоский массив,
   * и { data: [...] }, и вложенный ключ
   */
  private extractUserQualifications(response): IUserQualification[] {
    if (!response) {
      return [];
    }
    if (Array.isArray(response)) {
      return response;
    }
    if (Array.isArray(response.data)) {
      return response.data;
    }
    if (response.data && Array.isArray(response.data.qualifications)) {
      return response.data.qualifications;
    }
    if (response.data && Array.isArray(response.data.users_qualifications)) {
      return response.data.users_qualifications;
    }

    return [];
  }

  /**
   * Актуализация квалификаций сотрудника: work_type_ids — это полный набор
   * работ, которые сотрудник может выполнять. Что уже есть — остаётся,
   * новое добавится, отсутствующее удалится. Пустой массив снимает все
   */
  actualizeUserQualifications(userId: number | string, workTypeIds: string[]): Observable<any> {
    return this.http.post(`/admin/users/${userId}/actualize_qualifications/`, { work_type_ids: workTypeIds || [] });
  }

  getServiceFunctions() {
    return this.http.get(`/admin/functions/`);
  }

  getServiceFunction(id) {
    return this.http.get(`/admin/functions/${id}/all_versions/`);
  }

  getBranches(mr_region) {
    return this.plotsService.getBranches(mr_region);
  }

  getCities(params) {
    return this.plotsService.getCities(params);
  }

  getPlots(mr_id) {
    return this.plotsService.getPlots({ branch_id: mr_id });
  }

  createServiceFunction(data) {
    return this.http.post(`/admin/functions/`, data);
  }

  getUtcShift(city_id) {
    return this.http.get(`/admin/cities/${city_id}`);
  }

  getUserTypes() {
    return this.http.get(`/admin/employee_types/`);
  }

  getUserStatuses() {
    return this.http.get(`/admin/employee_statuses/`);
  }

  getRoles() {
    return this.http.get(`/admin/groups/`);
  }

  updateServiceFunction(data) {
    return this.http.post(`/admin/functions/`, data);
  }

  updateServiceFunctionStatus(id, data) {
    return this.http.patch(`/admin/functions/${id}/`, data);
  }

  getUserRoleExpanded(params) {
    return this.http.get(`/admin/users/user_group_sectors/`, {
      params: params,
    });
  }

  getMiscRegions() {
    return this.http.get(`/admin/users_misc_regions`);
  }

  getMiscBranches(params) {
    return this.http.get(`/admin/users_misc_branches`, { params: params });
  }

  getMiscSectors(params) {
    return this.http.get(`/admin/users_misc_sectors`, { params: params });
  }

  getCreateUserUnion() {
    const companies = this.companiesService.getCompaniesFilter({
      verbose: 1,
    });
    const functions = this.getServiceFunctions();
    const regions = this.getMiscRegions();
    const userTypes = this.getUserTypes();
    const userStatuses = this.getUserStatuses();
    const roles = this.getRoles();

    return forkJoin([companies, functions, regions, userTypes, userStatuses, roles]);
  }

  getEditUserUnion(id) {
    const companies = this.companiesService.getCompaniesFilter({
      verbose: 1,
    });
    const functions = this.getServiceFunctions();
    const regions = this.getMiscRegions();
    const userTypes = this.getUserTypes();
    const userStatuses = this.getUserStatuses();
    const roles = this.getRoles();
    const allVersions = this.getVersions(id, null);

    return forkJoin([companies, functions, regions, userTypes, userStatuses, roles, allVersions]);
  }

  createOrUpdateUser(user_data) {
    return this.http.post(`/async/user/create`, user_data);
  }

  checkDuplicates(params): Observable<ICheckEmployeeResponse> {
    return this.http.get<ICheckEmployeeResponse>('/employee/employee_check', {
      params,
    });
  }

  getVersions(id, version_number) {
    return this.http.get(`/admin/users/${id}/all_versions/`, {
      params: { version_number: version_number || '' },
    });
  }

  uploadUserPhoto(employee_id, photo_data) {
    return this.http.post(`/admin/users/${employee_id}/userpic/`, photo_data);
  }

  getFilterDataUnion() {
    const regions = this.plotsService.getRegions();
    const branches = this.plotsService.getBranches(null);
    const sectors = this.plotsService.getFilterPlots(null);
    const companies = this.companiesService.getCompaniesFilter();
    const userStatuses = this.getUserStatuses();
    const roles = this.getRoles();

    return forkJoin([regions, branches, sectors, companies, userStatuses, roles]);
  }

  getUsers(params) {
    return this.http.get(`/admin/users/`, { params: params });
  }

  getUserExpandedData(u_id) {
    return this.http.get(`/admin/users/${u_id}`);
  }

  checkPartner(data) {
    return this.http.post(`/async/user/check_partner`, data);
  }
}
